<template>
  <v-dialog v-model="dialog" max-width="600" persistent>
    <v-card>
      <v-card-title>
        <span class="headline">{{ isEditing ? 'Edit' : 'Create' }} Package Type</span>
      </v-card-title>

      <v-card-text>
        <v-form ref="form" v-model="valid">
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="formData.name"
                  label="Package Name"
                  :rules="[rules.required, rules.maxLength]"
                  :error-messages="nameError"
                  @blur="checkNameUnique"
                  outlined
                  dense
                  required
                />
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="formData.description"
                  label="Description (Optional)"
                  outlined
                  dense
                  rows="2"
                />
              </v-col>

              <v-col cols="12">
                <div class="subtitle-2 mb-2">Dimensions (inches)</div>
              </v-col>

              <v-col cols="4">
                <v-text-field
                  v-model.number="formData.length"
                  label="Length"
                  :rules="[rules.required, rules.positiveNumber]"
                  type="number"
                  step="0.01"
                  outlined
                  dense
                  required
                  suffix="in"
                />
              </v-col>

              <v-col cols="4">
                <v-text-field
                  v-model.number="formData.width"
                  label="Width"
                  :rules="[rules.required, rules.positiveNumber]"
                  type="number"
                  step="0.01"
                  outlined
                  dense
                  required
                  suffix="in"
                />
              </v-col>

              <v-col cols="4">
                <v-text-field
                  v-model.number="formData.height"
                  label="Height"
                  :rules="[rules.required, rules.positiveNumber]"
                  type="number"
                  step="0.01"
                  outlined
                  dense
                  required
                  suffix="in"
                />
              </v-col>

              <v-col cols="6">
                <v-text-field
                  v-model.number="formData.weight"
                  label="Weight (Optional)"
                  :rules="[rules.positiveNumberOptional]"
                  type="number"
                  step="0.01"
                  outlined
                  dense
                  suffix="lbs"
                />
              </v-col>

              <v-col cols="6">
                <v-switch
                  v-model="formData.isActive"
                  label="Active"
                  color="primary"
                />
              </v-col>

              <!-- Dimension Preview -->
              <v-col cols="12">
                <v-alert
                  type="info"
                  dense
                  outlined
                  class="mt-2"
                >
                  <div class="d-flex justify-space-between align-center">
                    <div>
                      <strong>Volume:</strong>
                      {{ calculateVolume() }} cubic inches
                    </div>
                    <div>
                      <strong>Dimensional Weight:</strong>
                      {{ calculateDimWeight() }} lbs
                    </div>
                  </div>
                </v-alert>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="cancel">Cancel</v-btn>
        <v-btn
          color="primary"
          :disabled="!valid || saving"
          :loading="saving"
          @click="save"
        >
          {{ isEditing ? 'Update' : 'Create' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { defineComponent } from 'vue'
import { PackageTypeService } from '@/services/PackageTypeService'

export default defineComponent({
  name: 'PackageTypeDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    packageType: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      valid: false,
      saving: false,
      packageService: null,
      nameError: '',
      formData: {
        name: '',
        description: '',
        length: null,
        width: null,
        height: null,
        weight: null,
        isActive: true
      },
      rules: {
        required: v => !!v || 'Required',
        maxLength: v => (!v || v.length <= 100) || 'Max 100 characters',
        positiveNumber: v => {
          if (!v && v !== 0) return 'Required'
          return v > 0 || 'Must be greater than 0'
        },
        positiveNumberOptional: v => {
          if (!v && v !== 0) return true
          return v > 0 || 'Must be greater than 0'
        }
      }
    }
  },
  computed: {
    dialog: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    isEditing() {
      return !!this.packageType
    }
  },
  watch: {
    packageType: {
      handler(newVal) {
        if (newVal) {
          this.formData = {
            name: newVal.name,
            description: newVal.description || '',
            length: newVal.length,
            width: newVal.width,
            height: newVal.height,
            weight: newVal.weight || null,
            isActive: newVal.isActive
          }
        } else {
          this.resetForm()
        }
      },
      immediate: true
    }
  },
  created() {
    this.packageService = new PackageTypeService()
  },
  methods: {
    calculateVolume() {
      const { length, width, height } = this.formData
      if (!length || !width || !height) return 0
      return Math.round(length * width * height * 100) / 100
    },
    calculateDimWeight() {
      const volume = this.calculateVolume()
      if (!volume) return 0
      return Math.ceil(volume / 139)
    },
    async checkNameUnique() {
      this.nameError = ''
      const name = this.formData.name
      
      if (!name) return
      if (this.isEditing && name === this.packageType?.name) return
      
      try {
        const exists = await this.packageService.checkNameExists(name)
        if (exists) {
          this.nameError = 'Package name already exists'
        }
      } catch (error) {
        console.error('Failed to check name uniqueness:', error)
      }
    },
    resetForm() {
      this.formData = {
        name: '',
        description: '',
        length: null,
        width: null,
        height: null,
        weight: null,
        isActive: true
      }
      this.nameError = ''
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    },
    cancel() {
      this.dialog = false
      this.resetForm()
    },
    async save() {
      if (!this.$refs.form.validate()) return
      
      // Check name uniqueness before saving
      await this.checkNameUnique()
      if (this.nameError) return

      this.saving = true
      try {
        if (this.isEditing) {
          await this.packageService.updatePackageType(
            this.packageType.packageTypeId,
            this.formData
          )
          this.$emit('show-snackbar', {
            text: 'Package type updated successfully',
            color: 'success'
          })
        } else {
          await this.packageService.createPackageType(this.formData)
          this.$emit('show-snackbar', {
            text: 'Package type created successfully',
            color: 'success'
          })
        }
        
        this.$emit('saved')
        this.cancel()
      } catch (error) {
        console.error('Failed to save package type:', error)
        this.$emit('show-snackbar', {
          text: error.response?.data?.error || 'Failed to save package type',
          color: 'error'
        })
      } finally {
        this.saving = false
      }
    }
  }
})
</script>